import React, {useEffect, useMemo} from "react";
import {useParams} from "react-router-dom";
import {BlockComponentContextProvider, styled} from "@reside/ui";
import {useSearchParam} from "react-use";

import {DocumentTitle} from "../document-title";
import {LayoutContent} from "../admin-layout";
import {AdmissionContextProvider} from "../admission-explorer/AdmissionContext";
import {admissionBlockComponents} from "../admission-explorer/AdmissionBlockComponentsContextProvider";
import {Header} from "./header";
import {Menu} from "./menu";
import {Slides} from "./slides";
import {useProcessedFileAnswers} from "./hooks/useProcessedFileAnswers";

export const AdminAdmissionExplorerRenderer = props => {
  const {sectionId} = useParams();
  const flagId = useSearchParam("flagId");

  const explorerProps = useMemo(
    () => ({
      draft: props.draft,
      uiState: props.uiState,
      uiActions: props.uiActions,
      draftActions: props.draftActions,
      notes: props.notes,
      user: props.user,
    }),
    [
      props.draft,
      props.uiState,
      props.uiActions,
      props.draftActions,
      props.notes,
      props.user,
    ],
  );

  useEffect(() => {
    const scrollToFlag = () => {
      if (flagId) {
        const element = document.getElementById(flagId);

        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }
    };

    if (sectionId) {
      explorerProps.uiActions.goToSection(sectionId);
      scrollToFlag();
    }
  }, [sectionId, flagId, explorerProps]);

  return (
    <>
      <DocumentTitle
        title={`Reside - Admin - Admission [${props.draft.instance.code}]`}
      />
      <Header
        draft={props.draft}
        onEdit={props.onEdit}
        onClose={props.onClose}
      />
      <Content>
        <Menu {...explorerProps} />
        <Slides {...explorerProps} />
      </Content>
    </>
  );
};

const Content = styled(LayoutContent)`
  flex-direction: row;
`;

export const AdminAdmissionExplorer = props => {
  const draftWithProcessedAnswers =
    useProcessedFileAnswers(props.draft) || props.draft;

  return (
    <AdmissionContextProvider {...props} draft={draftWithProcessedAnswers}>
      <BlockComponentContextProvider value={admissionBlockComponents}>
        <AdminAdmissionExplorerRenderer
          {...props}
          draft={draftWithProcessedAnswers}
        />
      </BlockComponentContextProvider>
    </AdmissionContextProvider>
  );
};
